// context/AuthContext.js
import { createContext, useContext, useState, useEffect } from "react";
import posthog from "posthog-js";
import { authService } from "../services/authService";
import { captureAnalyticsEvent, pushDataLayerEvent } from "../utils/analytics";
import { getUserEntitlements, PAYG_ROLE } from "../utils/entitlements";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función de logout
  const logout = async () => {
    
    try {
      
      await authService.logout();
      
    } catch (error) {
      console.error('❌ Logout error:', error);
    } finally {
      
      setToken(null);
      setUser(null);
      setError(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      // NOTA: Ya no usamos el sistema de simulaciones pendientes
      // Las simulaciones en caché persisten independientemente del logout
      
    }
  };

  // Debug: observar cambios en el estado de autenticación
  useEffect(() => {
    const isAuth = !!token && !!user;
    
  }, [token, user]);

  // Sincronizar el usuario autenticado con PostHog (independiente del consentimiento de cookies)
  useEffect(() => {
    if (!posthog.__loaded) return;

    if (user && token) {
      const distinctId = user.id || user._id || user.email;
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.name;

      if (distinctId) {
        posthog.identify(distinctId, {
          email: user.email,
          name: fullName || undefined,
          firstName: user.firstName,
          lastName: user.lastName,
          company: user.company,
        });
      }
    } else {
      posthog.reset();
    }
  }, [user, token]);

  // Inicializar el contexto al cargar
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('user');
        
        if (storedToken && storedUser) {
          // Hidratar ya: no bloquear la UI con "Verificando sesión…"
          // mientras verifyToken corre en background.
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          setIsLoading(false);
          
          // Verificar que el token sigue siendo válido (con auto-refresh si expirado)
          const verification = await authService.verifyToken();
          if (!verification.success) {
            // Token inválido y no se pudo refrescar, limpiar estado
            await logout();
          } else {
            // Refrescar rol/entitlements desde verify-token: cubre la
            // promoción payg→user tras suscribirse a Ecotrim (IT-639) sin
            // re-login. Se mergea sobre el usuario guardado para no perder
            // campos que verify-token no devuelve (company, phone, team…).
            const freshUser = verification?.data?.user;
            if (freshUser) {
              const merged = { ...JSON.parse(storedUser), ...freshUser };
              localStorage.setItem('user', JSON.stringify(merged));
              setUser(merged);
            }
            // Si el token fue refrescado durante verifyToken, actualizar estado
            const currentToken = localStorage.getItem('authToken');
            if (currentToken !== storedToken) {
              setToken(currentToken);
            }
          }
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        await logout();
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // Escuchar eventos de token refrescado automáticamente
  useEffect(() => {
    const handleTokenRefreshed = (event) => {
      const { token: newToken, user: newUser } = event.detail;
      
      setToken(newToken);
      if (newUser) setUser(newUser);
    };

    const handleSessionExpired = async () => {
      setToken(null);
      setUser(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');

      try {
        await authService.logout();
      } catch (error) {
        console.error('Error during session expiry logout:', error);
      }
    };

    window.addEventListener('auth:token-refreshed', handleTokenRefreshed);
    window.addEventListener('auth:session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('auth:token-refreshed', handleTokenRefreshed);
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, []);

  // Renovación proactiva del token cada 6 horas
  useEffect(() => {
    if (!token) return;

    const REFRESH_INTERVAL = 6 * 60 * 60 * 1000; // 6 horas

    const refreshTimer = setInterval(async () => {
      try {
        
        const verification = await authService.verifyToken();
        if (!verification.success) {
          console.warn('⚠️ No se pudo renovar el token proactivamente');
        }
      } catch (error) {
        console.error('Error en renovación proactiva:', error);
      }
    }, REFRESH_INTERVAL);

    return () => clearInterval(refreshTimer);
  }, [token]);

  // Función de login
  const login = async (email, password) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.login(email, password);
      
      if (response.success) {
        // Guardar token y usuario en localStorage
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        setToken(response.data.token);
        setUser(response.data.user);
        
        // NOTA: La asociación de simulaciones ahora se maneja automáticamente
        // mediante el useEffect en EnergyCalculator.js que detecta cuando
        // el usuario se autentica y crea la simulación de usuario desde el caché
        
        return { success: true, user: response.data.user };
      }
      
      throw new Error(response.error || 'Login failed');
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Login con Google (ID token)
  const loginWithGoogle = async (idToken, selectedPlan) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await authService.loginWithGoogle(idToken, selectedPlan);
      if (response.success) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setToken(response.data.token);
        setUser(response.data.user);

        if (response.data.isNewUser) {
          const userId = response.data.user._id || response.data.user.id;
          pushDataLayerEvent('registro_completado', `registro_completado:${userId}`);
          captureAnalyticsEvent('user_registered', {
            user_id: userId,
            email: response.data.user.email,
            full_name: [response.data.user.firstName, response.data.user.lastName].filter(Boolean).join(' '),
            company: response.data.user.company || null,
            registration_date: new Date().toISOString(),
            method: 'google',
          });
        }

        return { success: true, user: response.data.user };
      }
      throw new Error(response.error || 'Google login failed');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Función de registro
  const register = async (userData) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.register(userData);
      
      if (response.success) {
        // Guardar token y usuario en localStorage
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        setToken(response.data.token);
        setUser(response.data.user);
        
        const userId = response.data.user._id || response.data.user.id;

        pushDataLayerEvent('registro_completado', `registro_completado:${userId}`);

        // Track evento de registro en PostHog
        captureAnalyticsEvent('user_registered', {
          user_id: userId,
          email: response.data.user.email,
          full_name: [response.data.user.firstName, response.data.user.lastName].filter(Boolean).join(' '),
          company: response.data.user.company || null,
          registration_date: new Date().toISOString()
        });
        
        // NOTA: La asociación de simulaciones ahora se maneja automáticamente
        // mediante el useEffect en EnergyCalculator.js que detecta cuando
        // el usuario se autentica y crea la simulación de usuario desde el caché
        
        return { success: true, user: response.data.user };
      }
      
      throw new Error(response.error || 'Registration failed');
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Actualizar perfil
  const updateProfile = async (profileData) => {
    try {
      setError(null);
      const response = await authService.updateProfile(profileData);
      
      if (response.success) {
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      }
      
      throw new Error(response.error || 'Profile update failed');
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Cambiar contraseña
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      const response = await authService.changePassword(currentPassword, newPassword);
      
      if (response.success) {
        return { success: true };
      }
      
      throw new Error(response.error || 'Password change failed');
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Verificar autenticación
  const isAuthenticated = !!token && !!user;
  const isAdmin = user?.role === 'admin';
  // Pago por uso (IT-605): plan/features del usuario
  const isPayg = user?.role === PAYG_ROLE;
  // Super admin de equipo (IT-666): flag calculado por calc-api (verify-token)
  const isTeamAdmin = user?.isTeamAdmin === true;
  // Admin de plataforma (IT-700): acceso al panel trimergy-admin integrado.
  // Flag booleano propio en la colección users, independiente de role.
  const isPlatformAdmin = user?.platformAdmin === true;
  const entitlements = getUserEntitlements(user);
  const hasFeature = (feature) => entitlements[feature] === true;

  const value = {
    user,
    token,
    isLoading,
    error,
    isAuthenticated,
    isAdmin,
    isPayg,
    isTeamAdmin,
    isPlatformAdmin,
    entitlements,
    hasFeature,
    login,
    loginWithGoogle,
    register,
    logout,
    updateProfile,
    changePassword,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
